import type { ReactNode } from 'react';
import { Icon } from '../icons';
import type { IconName } from '../icons';

/**
 * Callout: a message that belongs to the page rather than to one field or
 * row (CO-88). It replaces `.notice`, `.alert`, `.banner` and the ad-hoc
 * tinted boxes (audit C1). Four tones, each with its own icon so the tone
 * is not carried by colour alone; the title says what happened, the body
 * what it means, the actions what to do.
 *
 * `role` defaults to "alert" for danger (announced at once) and "status"
 * for the rest.
 */
export type CalloutTone = 'info' | 'success' | 'warning' | 'danger';

const toneIcon: Record<CalloutTone, IconName> = {
  info: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
  danger: 'alert-circle',
};

export function Callout({
  tone = 'info',
  title,
  children,
  actions,
  role,
  testId,
  className,
}: {
  tone?: CalloutTone;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  role?: 'alert' | 'status' | 'note';
  testId?: string;
  className?: string;
}) {
  return (
    <div
      className={`callout callout--${tone}${className ? ` ${className}` : ''}`}
      role={role ?? (tone === 'danger' ? 'alert' : 'status')}
      data-testid={testId}
    >
      <Icon name={toneIcon[tone]} size={16} className="callout__icon" />
      <div className="callout__content">
        {title ? <strong className="callout__title">{title}</strong> : null}
        {children ? <div className="callout__body">{children}</div> : null}
        {actions ? <div className="callout__actions">{actions}</div> : null}
      </div>
    </div>
  );
}
